// Per-tab impersonation token store (sessionStorage) and session-aware role store.
//
// Design contract:
//   - IMP_TOKENS_KEY lives only in sessionStorage. It is set when this tab starts
//     impersonating a user via FakeLoginForm and cleared on exit-impersonation.
//     Tab close naturally destroys it; no cross-tab bleed is possible.
//   - ROLE_KEY is written to BOTH sessionStorage (primary) and localStorage (seed).
//     New tabs inherit the seed; each tab then maintains its own session copy.
//   - All readers prefer sessionStorage; localStorage is the fallback.

import type { Tokens } from './AuthSession';

export const IMP_TOKENS_KEY = 'imp-tokens';
export const IMP_FP_KEY = 'imp-fp';
export const ROLE_SESSION_KEY = 'role';
export const CURRENT_USER_KEY = 'current-user';

// --- impersonation tokens ---

export const readImpTokens = (): Tokens | null => {
  const raw = sessionStorage.getItem(IMP_TOKENS_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return null;
  }
};

export const saveImpTokens = (tokens: Tokens): void => {
  sessionStorage.setItem(IMP_TOKENS_KEY, JSON.stringify(tokens));
};

export const clearImpTokens = (): void => {
  sessionStorage.removeItem(IMP_TOKENS_KEY);
};

// --- impersonation fingerprint ---
// Separate from IMP_TOKENS_KEY so token rotation paths don't have to preserve it on every write.

export const readImpFp = (): string | null => {
  return sessionStorage.getItem(IMP_FP_KEY);
};

export const saveImpFp = (fp: string): void => {
  sessionStorage.setItem(IMP_FP_KEY, fp);
};

export const clearImpFp = (): void => {
  sessionStorage.removeItem(IMP_FP_KEY);
};

export const isImpersonating = (): boolean => {
  return sessionStorage.getItem(IMP_TOKENS_KEY) !== null;
};

// --- session-aware access token read ---
// Prefers sessionStorage imp-tokens; falls back to localStorage via the
// existing auth.readToken() path. Call sites that previously called
// auth.readToken() directly can migrate to this for session-awareness.

export const readAccessToken = (): string | null => {
  const imp = readImpTokens();
  if (imp) {
    return imp.accessToken;
  }
  // Delegate to the legacy key; avoid importing utils/auth.js (circular) --
  // read the keys directly instead.
  const email = localStorage.getItem(CURRENT_USER_KEY);
  if (!email) {
    return null;
  }
  const raw = localStorage.getItem('jwt-tokens');
  if (!raw) {
    return null;
  }
  try {
    const tokens = JSON.parse(raw) as Record<string, string>;
    return tokens[email] ?? null;
  } catch {
    return null;
  }
};

export const readRefreshToken = (): string | null => {
  const imp = readImpTokens();
  if (imp) {
    return imp.refreshToken;
  }
  const email = localStorage.getItem(CURRENT_USER_KEY);
  if (!email) {
    return null;
  }
  const raw = localStorage.getItem('jwt-refresh-tokens');
  if (!raw) {
    return null;
  }
  try {
    const tokens = JSON.parse(raw) as Record<string, string>;
    return tokens[email] ?? null;
  } catch {
    return null;
  }
};

// --- session-aware role ---

export const readSessionRole = (): string | null => {
  return sessionStorage.getItem(ROLE_SESSION_KEY) ?? localStorage.getItem(ROLE_SESSION_KEY);
};

export const saveSessionRole = (role: string): void => {
  sessionStorage.setItem(ROLE_SESSION_KEY, role);
  localStorage.setItem(ROLE_SESSION_KEY, role);
};

export const clearSessionRole = (): void => {
  sessionStorage.removeItem(ROLE_SESSION_KEY);
};

// --- cross-tab logout watcher ---
// Pure function: given a StorageEvent, decide what action this tab should take.
// Extracted to a pure function so it can be unit-tested without side effects.

export type TabLogoutAction = 'logout' | 'ignore';

export const resolveTabLogoutAction = (event: StorageEvent): TabLogoutAction => {
  if (event.key !== CURRENT_USER_KEY && event.key !== 'jwt-tokens') {
    return 'ignore';
  }
  // Another tab cleared the admin's localStorage session.
  if (event.newValue !== null) {
    return 'ignore';
  }
  // This tab is impersonating -- it has its own session; keep going.
  if (isImpersonating()) {
    return 'ignore';
  }
  return 'logout';
};
